import { uniqueId } from 'lodash';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BomPartType, ElementType } from 'bom/enums';
import { IBomPart, IElement } from 'bom/types';
import { Form } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { useJobPartActions } from 'jobs/hooks';
import { PartNumberHelper } from 'parts/helpers';
import { Size } from 'theme/enums';

import { mapManageElementsData } from './mappers';
import { manageElementsSchema } from './schema';
import { Table } from './Table';
import { IManageElementsData } from './type';

interface IProps {
  bomPart: IBomPart;
  isOpen: boolean;
  onClose: () => void;
}

export const ManageElements = ({ isOpen, onClose, bomPart }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const { upsertJobPart } = useJobPartActions({
    notificationPosition: NotificationPosition.DRAWER,
  });

  const defaultValues = useMemo(
    () => ({
      elements: sortedElements(bomPart.elements).map((element) => ({
        id: uniqueId(),
        line: element.line,
        element,
        quantity: element.quantity,
        quantityPerElement: bomPart.quantitiesPerElement[element.id],
        deleted: false,
      })),
    }),
    [bomPart.elements, bomPart.quantitiesPerElement]
  );

  const onSubmit =
    (reset: UseFormReset<IManageElementsData>) => async (data: IManageElementsData) => {
      const { success } = await upsertJobPart(mapManageElementsData(data, bomPart));

      if (!success) {
        return;
      }

      setTimeout(() => {
        clearNotification();
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IManageElementsData>) => {
    reset?.();
    clearNotification();
    onClose();
  };

  const subtitle = useMemo(() => {
    if (bomPart.type === BomPartType.COMPONENT) {
      return t('subtitleManageElementsComponent', {
        componentName: bomPart.componentName,
      });
    }

    return t('subtitleManageElementsPart', {
      partNumber: PartNumberHelper.toLabel(bomPart.partArr?.number),
    });
  }, [bomPart.type, bomPart.partArr?.number, bomPart.componentName, t]);

  return (
    <FormProvider schema={manageElementsSchema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        title={t('titleManageElements')}
        subtitle={subtitle}
        size={Size.LG}
        isOpen={isOpen}
        onSubmit={onSubmit}
        onClose={handleOnClose}
        notificationPosition={NotificationPosition.DRAWER}
        fieldWrapperSxProps={{ height: '60%' }}
        shouldSubmitOnKeyDown={false}
      >
        <Table bomPart={bomPart} />
      </Form>
    </FormProvider>
  );
};

const sortedElements = (elements: IElement[]) =>
  elements.sort((a: IElement, b: IElement) => {
    // Overage/Shortage first, then No Element Assigned (empty=1) second, then regular elements
    const aIsOverageOrShortage =
      a.id === ElementType.ID_OVERAGE || a.id === ElementType.ID_SHORTAGE;
    const bIsOverageOrShortage =
      b.id === ElementType.ID_OVERAGE || b.id === ElementType.ID_SHORTAGE;

    if (aIsOverageOrShortage && !bIsOverageOrShortage) {
      return -1;
    }

    if (!aIsOverageOrShortage && bIsOverageOrShortage) {
      return 1;
    }

    if (a.empty === 1 && b.empty !== 1) {
      return -1;
    }

    if (a.empty !== 1 && b.empty === 1) {
      return 1;
    }

    return 0;
  });
